import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, OCCASIONS } from "@/lib/api";
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  Sparkle,
  Cake,
  Calendar as CalendarIcon,
  ShieldCheck,
  CreditCard,
  UsersThree,
  Clock,
  ForkKnife,
  Coffee,
  Ticket as TicketIcon,
  Confetti,
  MinusCircle,
  PlusCircle,
} from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const STEPS = [
  { key: "occasion", label: "Occasion" },
  { key: "when", label: "Date & Slot" },
  { key: "decor", label: "Decor" },
  { key: "addons", label: "Add-ons" },
  { key: "details", label: "Details" },
  { key: "otp", label: "Verify" },
  { key: "payment", label: "Payment" },
];

function tomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function parseISODate(value) {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function toISODate(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function Booking() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [step, setStep] = useState(0);

  const [config, setConfig] = useState(null);
  const [packages, setPackages] = useState([]);
  const [foodMenu, setFoodMenu] = useState([]);
  const [bevMenu, setBevMenu] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    occasion: search.get("occasion") || "birthday",
    booking_date: tomorrowISO(),
    duration_hours: 2,
    members: 4,
    slot: "",
    package_id: null,
    package: null,
    include_cake: true,
    food_ids: [],
    bev_ids: [],
    offer_code: "",
    customer_name: "",
    phone: "",
    email: "",
    guests: 4,
    cake_message: "",
    special_requests: "",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpDev, setOtpDev] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);

  const [offerApplied, setOfferApplied] = useState(null); // {code, discount, description}
  const [offerChecking, setOfferChecking] = useState(false);

  const [showFoodDrawer, setShowFoodDrawer] = useState(false);
  const [showBevDrawer, setShowBevDrawer] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/food-items").then((r) => setFoodMenu(r.data)).catch(() => {});
    api.get("/beverage-items").then((r) => setBevMenu(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!data.booking_date || !data.duration_hours) return;
    api
      .get(`/slots?booking_date=${data.booking_date}&duration_hours=${data.duration_hours}`)
      .then((r) => setSlots(r.data))
      .catch(() => setSlots([]));
    setData((d) => ({ ...d, slot: "" }));
  }, [data.booking_date, data.duration_hours]);

  // ---- Live pricing ----
  const hallPrice = useMemo(() => {
    const d = config?.durations?.find((x) => x.hours === Number(data.duration_hours));
    return d ? d.hall_price : 2000;
  }, [data.duration_hours, config]);

  const memberExtra = useMemo(() => {
    const m = config?.members?.find((x) => x.members === Number(data.members));
    return m ? Number(m.extra) : 0;
  }, [data.members, config]);

  const packagePrice = data.package?.price || 0;
  const cakePrice = config?.cake_price || 250;
  const cakeDeduction = data.include_cake ? 0 : cakePrice;

  const foodTotal = useMemo(
    () => data.food_ids.reduce((s, id) => s + (foodMenu.find((f) => f.id === id)?.price || 0), 0),
    [data.food_ids, foodMenu]
  );
  const bevTotal = useMemo(
    () => data.bev_ids.reduce((s, id) => s + (bevMenu.find((b) => b.id === id)?.price || 0), 0),
    [data.bev_ids, bevMenu]
  );

  const subtotal = hallPrice + memberExtra + packagePrice + foodTotal + bevTotal - cakeDeduction;
  const discount = offerApplied?.discount || 0;
  const total = Math.max(subtotal - discount, 0);
  const advance = Math.round(total * (config?.advance_percent || 0.3));

  const occ = OCCASIONS.find((o) => o.key === data.occasion);
  const welcomeDrink = config?.welcome_drinks?.[data.occasion] || "House Sparkler";

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const stepValid = () => {
    if (step === 0) return !!data.occasion;
    if (step === 1) return !!data.booking_date && !!data.slot && !!data.duration_hours && !!data.members;
    if (step === 2) return true;
    if (step === 3) return true;
    if (step === 4) return data.customer_name.trim().length >= 2 && /^\+?[0-9 ]{8,}$/.test(data.phone.trim());
    if (step === 5) return otpVerified;
    return true;
  };

  const applyOffer = async () => {
    if (!data.offer_code.trim()) return;
    setOfferChecking(true);
    try {
      const r = await api.post("/offer/validate", {
        code: data.offer_code.trim(),
        subtotal,
        occasion: data.occasion,
      });
      setOfferApplied(r.data);
      toast.success(`Offer applied · you save ₹${r.data.discount}`);
    } catch (e) {
      setOfferApplied(null);
      toast.error(e.response?.data?.detail || "Invalid offer code");
    } finally {
      setOfferChecking(false);
    }
  };
  const removeOffer = () => { setOfferApplied(null); setData({ ...data, offer_code: "" }); };

  const sendOtp = async () => {
    setLoading(true);
    try {
      const r = await api.post("/otp/send", { phone: data.phone.trim() });
      setOtpSent(true);
      if (r.data.dev_mode && r.data.otp) {
        setOtpDev(r.data.otp);
        toast.success(`OTP sent (dev mode): ${r.data.otp}`);
      } else {
        toast.success("OTP sent to your phone.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      await api.post("/otp/verify", { phone: data.phone.trim(), code: otpCode.trim() });
      setOtpVerified(true);
      toast.success("Phone verified");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const createBooking = async () => {
    setLoading(true);
    try {
      const payload = {
        customer_name: data.customer_name,
        phone: data.phone.trim(),
        email: data.email,
        occasion: data.occasion,
        booking_date: data.booking_date,
        slot: data.slot,
        duration_hours: Number(data.duration_hours),
        members: Number(data.members),
        guests: Number(data.guests) || Number(data.members),
        package_id: data.package_id,
        include_cake: data.include_cake,
        cake_message: data.cake_message,
        food_item_ids: data.food_ids,
        beverage_item_ids: data.bev_ids,
        offer_code: offerApplied ? offerApplied.code : "",
        special_requests: data.special_requests,
      };
      const r = await api.post("/bookings", payload);
      setBooking(r.data);
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create booking");
      throw e;
    } finally { setLoading(false); }
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const payNow = async () => {
    setLoading(true);
    try {
      const b = booking || (await createBooking());
      const orderR = await api.post("/payment/create-order", { booking_id: b.id });
      const order = orderR.data;

      if (order.mock) {
        await api.post("/payment/verify", {
          booking_id: b.id,
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `pay_MOCK${Date.now()}`,
          razorpay_signature: "mock_signature",
        });
        toast.success("Payment received (mock). Booking confirmed!");
        navigate(`/confirmation/${b.reference}`);
        return;
      }

      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Failed to load Razorpay. Please try again."); return; }
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "MoonGlow Screens",
        description: `Booking ${b.reference}`,
        order_id: order.order_id,
        prefill: { name: data.customer_name, email: data.email, contact: data.phone },
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/payment/verify", {
              booking_id: b.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful. Booking confirmed!");
            navigate(`/confirmation/${b.reference}`);
          } catch (e) {
            toast.error("Payment verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      };
      new window.Razorpay(opts).open();
    } catch (e) {
      // handled inside createBooking
    } finally { setLoading(false); }
  };

  const toggle = (arr, id) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  return (
    <div className="min-h-screen bg-mg text-white">
      <Header />
      <div className="pt-24 sm:pt-28 pb-24 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Stepper */}
        <div className="flex items-center justify-between overflow-x-auto gap-2 pb-1">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1 min-w-[100px]">
              <div
                data-testid={`step-${s.key}`}
                className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${
                  i <= step ? "text-gold" : "text-white/40"
                }`}
              >
                {String(i + 1).padStart(2, "0")} · {s.label}
              </div>
              <div
                className={`mt-2 h-[3px] rounded ${i <= step ? "" : "bg-white/10"}`}
                style={i <= step ? { backgroundColor: "var(--mg-gold)" } : {}}
              />
            </div>
          ))}
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Step 0 - Occasion */}
                {step === 0 && (
                  <div>
                    <h1 className="font-display text-3xl sm:text-4xl">What are we celebrating?</h1>
                    <p className="mt-2 text-white/60">Choose the occasion — we'll style the hall accordingly.</p>
                    <div className="grid sm:grid-cols-2 gap-4 mt-8">
                      {OCCASIONS.map((o) => (
                        <button
                          key={o.key}
                          data-testid={`choose-occasion-${o.key}`}
                          onClick={() => setData({ ...data, occasion: o.key })}
                          className={`card-lux rounded-lg p-4 sm:p-5 text-left flex items-center gap-4 ${
                            data.occasion === o.key ? "border-gold" : ""
                          }`}
                          style={data.occasion === o.key ? { borderColor: "var(--mg-gold)" } : {}}
                        >
                          <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 border border-mg">
                            <img src={o.img.fallback} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1">
                            <div className="font-display text-lg">{o.label}</div>
                            <div className="text-xs text-white/60 mt-1">{o.tag}</div>
                          </div>
                          {data.occasion === o.key && <CheckCircle size={22} weight="fill" className="text-gold" />}
                        </button>
                      ))}
                    </div>
                    {occ && config?.welcome_drinks?.[data.occasion] && (
                      <div className="mt-6 card-lux rounded-lg p-4 flex items-center gap-3">
                        <Confetti size={22} weight="duotone" className="text-gold" />
                        <div>
                          <div className="label-tiny">Complimentary welcome drink</div>
                          <div className="font-display text-lg mt-0.5">{welcomeDrink}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 1 - Date + Duration + Members + Slot */}
                {step === 1 && (
                  <div>
                    <h1 className="font-display text-3xl sm:text-4xl">Pick your night.</h1>
                    <p className="mt-2 text-white/60">Choose duration, group size, date and slot.</p>

                    {/* Duration */}
                    <div className="mt-8">
                      <div className="label-tiny flex items-center gap-2"><Clock size={12} /> Duration</div>
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        {(config?.durations || []).map((d) => (
                          <button
                            key={d.hours}
                            data-testid={`duration-${d.hours}`}
                            onClick={() => setData({ ...data, duration_hours: d.hours })}
                            className={`card-lux rounded-md p-4 text-left ${data.duration_hours === d.hours ? "border-gold" : ""}`}
                            style={data.duration_hours === d.hours ? { borderColor: "var(--mg-gold)" } : {}}
                          >
                            <div className="font-display text-xl">{d.label}</div>
                            <div className="text-xs text-white/60 mt-1">₹{d.hall_price.toLocaleString("en-IN")} hall</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Members */}
                    <div className="mt-8">
                      <div className="label-tiny flex items-center gap-2"><UsersThree size={12} /> Members</div>
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        {(config?.members || []).map((m) => (
                          <button
                            key={m.members}
                            data-testid={`members-${m.members}`}
                            onClick={() => setData({ ...data, members: m.members, guests: m.members })}
                            className={`card-lux rounded-md p-4 text-center ${data.members === m.members ? "border-gold" : ""}`}
                            style={data.members === m.members ? { borderColor: "var(--mg-gold)" } : {}}
                          >
                            <div className="font-display text-2xl">{m.members}</div>
                            <div className="label-tiny mt-1">Members</div>
                            <div className="text-[11px] text-gold mt-2">
                              {m.extra > 0 ? `+ ₹${m.extra}` : "Included"}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="mt-8">
                      <div className="label-tiny">Date</div>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="booking-date"
                            className="field mt-2 rounded-md px-4 py-3 w-full sm:w-72 flex items-center justify-between text-left"
                          >
                            <span>
                              {data.booking_date
                                ? new Date(`${data.booking_date}T00:00:00`).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "Select a date"}
                            </span>
                            <CalendarIcon size={18} className="text-gold" weight="duotone" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 border border-mg-2 bg-mg text-white shadow-2xl">
                          <Calendar
                            mode="single"
                            selected={parseISODate(data.booking_date)}
                            onSelect={(day) => setData({ ...data, booking_date: toISODate(day) })}
                            disabled={{ before: parseISODate(tomorrowISO()) }}
                            initialFocus
                            className="bg-mg text-white"
                            classNames={{
                              day_selected: "bg-gold text-black hover:bg-gold hover:text-black",
                              day_today: "bg-white/10 text-gold font-semibold",
                              day: "h-9 w-9 rounded-md",
                              caption_label: "text-white font-semibold",
                              nav_button: "border border-white/10 bg-transparent text-white hover:bg-white/10",
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Slots */}
                    <div className="mt-8">
                      <div className="label-tiny">Available Slots · {data.duration_hours}h</div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                        {slots.length === 0 && (
                          <div className="text-white/50 text-sm col-span-full">Loading slots…</div>
                        )}
                        {slots.map((s) => (
                          <button
                            key={s.slot}
                            data-testid={`slot-${s.slot.replace(/[^A-Za-z0-9]/g, "")}`}
                            disabled={!s.available}
                            onClick={() => setData({ ...data, slot: s.slot })}
                            className={`card-lux rounded-md p-3 text-left ${!s.available ? "opacity-40 cursor-not-allowed" : ""} ${data.slot === s.slot ? "border-gold" : ""}`}
                            style={data.slot === s.slot ? { borderColor: "var(--mg-gold)" } : {}}
                          >
                            <div className="flex items-center gap-2">
                              <CalendarIcon size={16} className="text-gold" weight="duotone" />
                              <div className="font-display text-sm">{s.slot}</div>
                              {!s.available && <span className="ml-auto label-tiny text-white/40">Booked</span>}
                              {data.slot === s.slot && <CheckCircle size={18} className="ml-auto text-gold" weight="fill" />}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2 - Decor */}
                {step === 2 && (
                  <div>
                    <h1 className="font-display text-3xl sm:text-4xl">Add a signature theme.</h1>
                    <p className="mt-2 text-white/60">Optional — the hall is beautiful even without.</p>
                    <div className="grid sm:grid-cols-2 gap-4 mt-8">
                      <button
                        data-testid="choose-package-none"
                        onClick={() => setData({ ...data, package_id: null, package: null })}
                        className={`card-lux rounded-lg p-5 text-left ${!data.package_id ? "border-gold" : ""}`}
                        style={!data.package_id ? { borderColor: "var(--mg-gold)" } : {}}
                      >
                        <div className="font-display text-lg">No decoration</div>
                        <div className="text-xs text-white/60 mt-1">Just the classic MoonGlow hall.</div>
                        <div className="mt-4 font-display text-xl text-white">Free</div>
                      </button>
                      {packages.map((p) => (
                        <button
                          key={p.id}
                          data-testid={`choose-package-${p.id}`}
                          onClick={() => setData({ ...data, package_id: p.id, package: p })}
                          className={`card-lux rounded-lg p-5 text-left ${data.package_id === p.id ? "border-gold" : ""}`}
                          style={data.package_id === p.id ? { borderColor: "var(--mg-gold)" } : {}}
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-display text-lg">{p.name}</div>
                            <Sparkle size={18} className="text-gold" weight="duotone" />
                          </div>
                          <div className="text-xs text-white/60 mt-1 leading-relaxed">{p.description}</div>
                          <div className="mt-4 font-display text-xl gold-gradient-text">+ ₹{p.price.toLocaleString("en-IN")}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3 - Add-ons: Cake, Food, Beverages, Welcome, Offer */}
                {step === 3 && (
                  <div>
                    <h1 className="font-display text-3xl sm:text-4xl">Complete your celebration.</h1>
                    <p className="mt-2 text-white/60">Cake, food, beverages & a special code — mix as you like.</p>

                    {/* Welcome drink */}
                    <div className="mt-8 card-lux rounded-lg p-5 flex items-center gap-4">
                      <Confetti size={24} weight="duotone" className="text-gold" />
                      <div className="flex-1">
                        <div className="label-tiny">Complimentary welcome drink</div>
                        <div className="font-display text-lg mt-1">{welcomeDrink}</div>
                      </div>
                      <div className="label-tiny text-gold">On the house</div>
                    </div>

                    {/* Cake */}
                    <div className="mt-6 card-lux rounded-lg p-5">
                      <div className="flex items-start gap-3">
                        <Cake size={26} weight="duotone" className="text-gold shrink-0" />
                        <div className="flex-1">
                          <div className="font-display text-lg">1/2 kg cake included</div>
                          <div className="text-xs text-white/60 mt-1">
                            Choose vanilla, chocolate, red velvet or butterscotch. Add a message below.
                          </div>

                          {data.include_cake ? (
                            <input
                              data-testid="cake-message"
                              value={data.cake_message}
                              onChange={(e) => setData({ ...data, cake_message: e.target.value })}
                              className="field mt-4 rounded-md px-4 py-3 w-full text-sm"
                              placeholder='Cake message · e.g. "Happy 25th, Sam!"'
                            />
                          ) : (
                            <div className="mt-3 text-xs text-green-400">
                              You'll save ₹{cakePrice} by skipping the cake.
                            </div>
                          )}

                          <label
                            data-testid="no-cake-checkbox-label"
                            htmlFor="no-cake-checkbox"
                            className="mt-4 flex items-start gap-3 cursor-pointer select-none"
                          >
                            <input
                              id="no-cake-checkbox"
                              data-testid="no-cake-checkbox"
                              type="checkbox"
                              checked={!data.include_cake}
                              onChange={(e) => setData({ ...data, include_cake: !e.target.checked })}
                              className="mt-0.5 h-4 w-4 rounded border border-mg accent-[color:var(--mg-gold)] shrink-0"
                            />
                            <span className="text-xs text-white/80 leading-relaxed">
                              I don't want a cake — <span className="text-gold">save ₹{cakePrice}</span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Food + Bev */}
                    <div className="grid sm:grid-cols-2 gap-4 mt-6">
                      <button
                        data-testid="open-food-drawer"
                        onClick={() => setShowFoodDrawer(true)}
                        className="card-lux rounded-lg p-5 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <ForkKnife size={22} weight="duotone" className="text-gold" />
                          <div className="font-display text-lg">Food menu</div>
                        </div>
                        <div className="text-xs text-white/60 mt-2">
                          {data.food_ids.length === 0
                            ? "Add pizzas, wings, desserts and more"
                            : `${data.food_ids.length} item(s) · ₹${foodTotal.toLocaleString("en-IN")}`}
                        </div>
                        <div className="mt-3 label-tiny text-gold inline-flex items-center gap-1">
                          {data.food_ids.length === 0 ? "Browse menu" : "Edit menu"} <CaretRight size={12} weight="bold" />
                        </div>
                      </button>

                      <button
                        data-testid="open-bev-drawer"
                        onClick={() => setShowBevDrawer(true)}
                        className="card-lux rounded-lg p-5 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <Coffee size={22} weight="duotone" className="text-gold" />
                          <div className="font-display text-lg">Beverages</div>
                        </div>
                        <div className="text-xs text-white/60 mt-2">
                          {data.bev_ids.length === 0
                            ? "Coffees, mocktails, juices, colas"
                            : `${data.bev_ids.length} item(s) · ₹${bevTotal.toLocaleString("en-IN")}`}
                        </div>
                        <div className="mt-3 label-tiny text-gold inline-flex items-center gap-1">
                          {data.bev_ids.length === 0 ? "Browse bar" : "Edit bar"} <CaretRight size={12} weight="bold" />
                        </div>
                      </button>
                    </div>

                    {/* Offer code */}
                    <div className="mt-6 card-lux rounded-lg p-5">
                      <div className="flex items-center gap-3">
                        <TicketIcon size={20} weight="duotone" className="text-gold" />
                        <div className="font-display text-lg">Offer code</div>
                      </div>
                      {offerApplied ? (
                        <div className="mt-3 flex items-center justify-between">
                          <div>
                            <div className="text-sm text-green-400">
                              {offerApplied.code} applied · you save ₹{offerApplied.discount}
                            </div>
                            <div className="text-xs text-white/60 mt-1">{offerApplied.description}</div>
                          </div>
                          <button
                            data-testid="remove-offer-btn"
                            onClick={removeOffer}
                            className="btn-ghost-gold rounded-md px-3 py-1.5 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3 flex gap-2">
                          <input
                            data-testid="offer-input"
                            value={data.offer_code}
                            onChange={(e) => setData({ ...data, offer_code: e.target.value.toUpperCase() })}
                            placeholder="MOONGLOW10, LOVE20, FIRST100…"
                            className="field rounded-md px-4 py-3 flex-1 text-sm tracking-widest uppercase"
                          />
                          <button
                            data-testid="apply-offer-btn"
                            onClick={applyOffer}
                            disabled={offerChecking || !data.offer_code.trim()}
                            className="btn-gold rounded-md px-4 py-2 text-xs disabled:opacity-50"
                          >
                            {offerChecking ? "Checking…" : "Apply"}
                          </button>
                        </div>
                      )}
                      <div className="mt-3 text-[11px] text-white/40">
                        Try: <span className="text-gold">MOONGLOW10</span> · <span className="text-gold">FIRST100</span> · <span className="text-gold">BIRTHDAY15</span> · <span className="text-gold">LOVE20</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4 - Details */}
                {step === 4 && (
                  <div>
                    <h1 className="font-display text-3xl sm:text-4xl">Tell us about you.</h1>
                    <p className="mt-2 text-white/60">We'll send SMS + email + WhatsApp confirmation on payment.</p>
                    <div className="grid sm:grid-cols-2 gap-4 mt-8">
                      <div>
                        <label className="label-tiny">Full Name</label>
                        <input data-testid="customer-name" value={data.customer_name}
                          onChange={(e) => setData({ ...data, customer_name: e.target.value })}
                          className="field mt-2 rounded-md px-4 py-3 w-full" placeholder="Your name" />
                      </div>
                      <div>
                        <label className="label-tiny">Phone (with country code)</label>
                        <input data-testid="customer-phone" value={data.phone}
                          onChange={(e) => setData({ ...data, phone: e.target.value })}
                          className="field mt-2 rounded-md px-4 py-3 w-full" placeholder="+91 9876543210" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="label-tiny">Email (for confirmation)</label>
                        <input data-testid="customer-email" value={data.email}
                          onChange={(e) => setData({ ...data, email: e.target.value })}
                          className="field mt-2 rounded-md px-4 py-3 w-full" placeholder="you@email.com" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="label-tiny">Special Requests</label>
                        <textarea data-testid="special-requests" value={data.special_requests}
                          onChange={(e) => setData({ ...data, special_requests: e.target.value })}
                          className="field mt-2 rounded-md px-4 py-3 w-full min-h-[100px]"
                          placeholder="Play a specific movie? Vegan cake? Let us know." />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 5 - OTP */}
                {step === 5 && (
                  <div>
                    <h1 className="font-display text-3xl sm:text-4xl">Verify your phone.</h1>
                    <p className="mt-2 text-white/60">We'll send a 6-digit OTP to <span className="text-gold">{data.phone || "your number"}</span>.</p>
                    {!otpSent ? (
                      <button data-testid="send-otp-btn" onClick={sendOtp} disabled={loading || !data.phone}
                        className="btn-gold rounded-md px-6 py-3 mt-8 text-sm inline-flex items-center gap-2">
                        {loading ? "Sending…" : "Send OTP"} <CaretRight size={14} weight="bold" />
                      </button>
                    ) : (
                      <div className="mt-8">
                        <label className="label-tiny">Enter OTP</label>
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <input data-testid="otp-input" value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                            className="field rounded-md px-4 py-3 w-48 tracking-[0.4em] text-center font-display text-xl"
                            placeholder="••••••" />
                          <button data-testid="verify-otp-btn" onClick={verifyOtp}
                            disabled={loading || otpCode.length !== 6 || otpVerified}
                            className="btn-gold rounded-md px-5 py-3 text-sm inline-flex items-center gap-2">
                            {otpVerified ? "Verified" : loading ? "Verifying…" : "Verify"}
                            {otpVerified ? <CheckCircle size={16} weight="fill" /> : <ShieldCheck size={16} weight="duotone" />}
                          </button>
                          <button data-testid="resend-otp-btn" onClick={sendOtp}
                            className="btn-ghost-gold rounded-md px-4 py-3 text-xs">
                            Resend
                          </button>
                        </div>
                        {otpDev && (
                          <div className="mt-4 text-xs text-white/50">
                            Dev mode: your OTP is <span className="text-gold font-semibold" data-testid="dev-otp-hint">{otpDev}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 6 - Payment */}
                {step === 6 && (
                  <div>
                    <h1 className="font-display text-3xl sm:text-4xl">Confirm & pay.</h1>
                    <p className="mt-2 text-white/60">Pay 30% now to lock your slot. Balance on arrival.</p>

                    <div className="card-lux rounded-lg p-6 mt-8 space-y-2 text-sm">
                      <Row k="Occasion" v={occ?.label} />
                      <Row k="Date" v={data.booking_date} />
                      <Row k="Duration · Slot" v={`${data.duration_hours}h · ${data.slot}`} />
                      <Row k="Members" v={String(data.members)} />
                      <Row k="Decor" v={data.package?.name || "—"} />
                      <Row k="Cake" v={data.include_cake ? "1/2 kg included" : "Not selected"} />
                      <Row k="Welcome Drink" v={welcomeDrink} />
                      <div className="divider-lux my-3" />
                      <Row k={`Hall (${data.duration_hours}h)`} v={`₹${hallPrice.toLocaleString("en-IN")}`} />
                      {memberExtra > 0 && <Row k={`Members (${data.members})`} v={`+ ₹${memberExtra.toLocaleString("en-IN")}`} />}
                      {packagePrice > 0 && <Row k="Decoration" v={`₹${packagePrice.toLocaleString("en-IN")}`} />}
                      {foodTotal > 0 && <Row k="Food" v={`₹${foodTotal.toLocaleString("en-IN")}`} />}
                      {bevTotal > 0 && <Row k="Beverages" v={`₹${bevTotal.toLocaleString("en-IN")}`} />}
                      {cakeDeduction > 0 && <Row k="Cake removed" v={`− ₹${cakeDeduction}`} />}
                      {discount > 0 && <Row k={`Offer · ${offerApplied?.code}`} v={`− ₹${discount}`} gold />}
                      <div className="divider-lux my-3" />
                      <Row k="Total" v={`₹${total.toLocaleString("en-IN")}`} strong />
                      <Row k="Advance (30%)" v={`₹${advance.toLocaleString("en-IN")}`} strong gold />
                    </div>

                    <button data-testid="pay-now-btn" onClick={payNow} disabled={loading}
                      className="btn-gold rounded-md px-6 py-3.5 mt-8 text-sm inline-flex items-center gap-2">
                      <CreditCard size={16} weight="duotone" /> {loading ? "Processing…" : `Pay ₹${advance.toLocaleString("en-IN")} to Confirm`}
                    </button>

                    {config && !config.razorpay_live && (
                      <div className="mt-4 text-xs text-white/50">
                        Payments in MOCK mode — booking will confirm instantly without a real charge.
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Nav */}
            <div className="flex items-center justify-between mt-10 sm:mt-12">
              <button data-testid="step-back" onClick={back} disabled={step === 0}
                className="btn-ghost-gold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-30">
                <CaretLeft size={14} weight="bold" /> Back
              </button>
              {step < STEPS.length - 1 && (
                <button data-testid="step-next" onClick={next} disabled={!stepValid()}
                  className="btn-gold rounded-md px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-40">
                  Continue <CaretRight size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>

          {/* Summary sidebar */}
          <aside className="lg:sticky lg:top-28 h-fit card-lux rounded-lg p-5 sm:p-6">
            <div className="label-tiny">Booking Summary</div>
            <div className="mt-3 space-y-1.5 text-sm text-white/80">
              <Row k="Occasion" v={occ?.label} />
              <Row k="Date" v={data.booking_date || "—"} />
              <Row k="Duration" v={`${data.duration_hours}h`} />
              <Row k="Slot" v={data.slot || "—"} />
              <Row k="Members" v={String(data.members)} />
              <Row k="Decor" v={data.package?.name || "—"} />
              <Row k="Cake" v={data.include_cake ? "1/2 kg" : "—"} />
              <Row k="Food" v={data.food_ids.length ? `${data.food_ids.length} items` : "—"} />
              <Row k="Beverages" v={data.bev_ids.length ? `${data.bev_ids.length} items` : "—"} />
              <Row k="Welcome" v={welcomeDrink} />
              {offerApplied && (
                <Row k="Offer" v={offerApplied.code} gold />
              )}
            </div>
            <div className="divider-lux my-5" />
            <Row k="Subtotal" v={`₹${subtotal.toLocaleString("en-IN")}`} />
            {discount > 0 && <Row k="Discount" v={`− ₹${discount}`} gold />}
            <Row k="Total" v={`₹${total.toLocaleString("en-IN")}`} strong data-testid="summary-total" />
            <Row k="Advance now" v={`₹${advance.toLocaleString("en-IN")}`} strong gold data-testid="summary-advance" />
            <div className="text-[11px] text-white/50 mt-3">
              Confirmation via SMS · WhatsApp · Email with venue location.
            </div>
          </aside>
        </div>
      </div>

      {/* Food drawer */}
      {showFoodDrawer && (
        <SideDrawer title="Choose your food" onClose={() => setShowFoodDrawer(false)} testid="food-drawer">
          <MenuList
            items={foodMenu}
            selected={data.food_ids}
            toggle={(id) => setData({ ...data, food_ids: toggle(data.food_ids, id) })}
            withVeg
          />
        </SideDrawer>
      )}
      {/* Beverage drawer */}
      {showBevDrawer && (
        <SideDrawer title="Choose your beverages" onClose={() => setShowBevDrawer(false)} testid="bev-drawer">
          <MenuList
            items={bevMenu}
            selected={data.bev_ids}
            toggle={(id) => setData({ ...data, bev_ids: toggle(data.bev_ids, id) })}
          />
        </SideDrawer>
      )}

      <Footer />
    </div>
  );
}

function Row({ k, v, strong, gold, ...rest }) {
  return (
    <div className="flex items-center justify-between text-sm" {...rest}>
      <span className="text-white/60">{k}</span>
      <span className={`${strong ? "font-display text-lg" : ""} ${gold ? "gold-gradient-text" : ""}`}>{v || "—"}</span>
    </div>
  );
}

function SideDrawer({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      <div data-testid={testid} className="relative w-full max-w-md h-full bg-mg-2 border-l border-mg overflow-y-auto">
        <div className="sticky top-0 bg-mg-2 border-b border-mg backdrop-blur-md flex items-center justify-between p-5 z-10">
          <div className="font-display text-xl">{title}</div>
          <button
            data-testid={`${testid}-close`}
            onClick={onClose}
            className="btn-ghost-gold rounded-md px-3 py-1.5 text-xs"
          >
            Close
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function MenuList({ items, selected, toggle, withVeg = false }) {
  // group by category
  const groups = items.reduce((acc, i) => {
    acc[i.category] = acc[i.category] || [];
    acc[i.category].push(i);
    return acc;
  }, {});
  return (
    <div className="space-y-8">
      {Object.entries(groups).map(([cat, arr]) => (
        <div key={cat}>
          <div className="label-tiny mb-3">{cat}</div>
          <div className="space-y-3">
            {arr.map((i) => {
              const on = selected.includes(i.id);
              return (
                <button
                  key={i.id}
                  data-testid={`menu-item-${i.id}`}
                  onClick={() => toggle(i.id)}
                  className={`w-full text-left card-lux rounded-md p-4 flex items-center gap-3 ${on ? "border-gold" : ""}`}
                  style={on ? { borderColor: "var(--mg-gold)" } : {}}
                >
                  {withVeg && (
                    <span className={`inline-block w-3 h-3 rounded-sm border ${i.is_veg ? "border-green-500" : "border-red-500"} relative shrink-0`}>
                      <span className={`absolute inset-0 m-auto w-1.5 h-1.5 rounded-full ${i.is_veg ? "bg-green-500" : "bg-red-500"}`} />
                    </span>
                  )}
                  <div className="flex-1">
                    <div className="text-sm">{i.name}</div>
                    {i.description && <div className="text-[11px] text-white/50 mt-0.5">{i.description}</div>}
                  </div>
                  <div className="text-sm text-gold shrink-0">₹{i.price}</div>
                  {on ? <MinusCircle size={22} className="text-gold shrink-0" weight="duotone" /> : <PlusCircle size={22} className="text-white/60 shrink-0" weight="duotone" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
